import { useState } from 'react';
import { ActionError, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { useConsole } from '../../data/console-context.tsx';
import { isConnectedByEachUser, type McpServerRow } from '../../data/rows.ts';
import { AddServerForm } from './add-server-form.tsx';

const HOW_USERS_CONNECT: Record<McpServerRow['credential'], string> = {
  session_token: 'From the session',
  oauth_refresh: 'Renewed by Plato',
  user_sign_in: 'Each user signs in',
  user_api_key: "Each user's API key",
};

export function ToolServers(props: {
  servers: readonly McpServerRow[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const [editing, setEditing] = useState<{ name: string; url: string } | null>(null);
  const [movingUsers, setMovingUsers] = useState<McpServerRow | null>(null);
  const [removing, setRemoving] = useState<McpServerRow | null>(null);
  const [adding, setAdding] = useState(false);
  const edit = (next: { name: string; url: string } | null) => {
    setEditing(next);
    setMovingUsers(null);
  };
  const run = (change: () => Promise<void>) =>
    void action.run(change).then((ok) => {
      if (ok) {
        edit(null);
        setRemoving(null);
        props.onChanged();
      }
    });
  const change = (name: string, update: { enabled?: boolean; url?: string }) =>
    run(() => data.updateMcpServer(tenant.id, name, update));
  const saveUrl = (server: McpServerRow, url: string) => {
    if (isConnectedByEachUser(server) && url !== server.url) setMovingUsers(server);
    else change(server.name, { url });
  };

  return (
    <section>
      <h2>Tool servers</h2>
      <p className="muted">
        Your own MCP server is registered with Plato, and the assistant calls it as the signed-in
        user. Admins can also add other services, which each of your users connects with their own
        account.
      </p>
      {!props.canEdit && (
        <p className="read-only-note">Changing tool servers needs the admin role.</p>
      )}
      <DataTable
        caption="MCP servers"
        columns={[
          { header: 'Name', cell: (row) => <ServerName server={row} /> },
          {
            header: 'URL',
            cell: (row) =>
              editing?.name === row.name ? (
                <form
                  className="row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveUrl(row, editing.url);
                  }}
                >
                  <input
                    type="url"
                    aria-label={`New URL for ${row.name}`}
                    value={editing.url}
                    onChange={(e) => edit({ name: row.name, url: e.target.value })}
                  />
                  <button type="submit" className="primary" disabled={action.pending}>
                    Save
                  </button>
                  <button type="button" className="quiet" onClick={() => edit(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                row.url
              ),
          },
          { header: 'How users connect', cell: (row) => HOW_USERS_CONNECT[row.credential] },
          {
            header: 'Status',
            cell: (row) => (
              <span className={row.enabled ? 'chip chip-success' : 'chip'}>
                {row.enabled ? 'On' : 'Off'}
              </span>
            ),
          },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: McpServerRow & { id: string }) => (
                    <div className="row">
                      <button
                        type="button"
                        disabled={action.pending}
                        onClick={() => change(row.name, { enabled: !row.enabled })}
                      >
                        {row.enabled ? 'Switch off' : 'Switch on'}
                      </button>
                      <button
                        type="button"
                        className="quiet"
                        onClick={() => edit({ name: row.name, url: row.url })}
                      >
                        Change URL
                      </button>
                      {isConnectedByEachUser(row) && (
                        <button
                          type="button"
                          className="danger"
                          disabled={action.pending}
                          aria-label={`Remove ${row.name}`}
                          onClick={() => setRemoving(row)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.servers.map((server) => ({ ...server, id: server.name }))}
      />
      {movingUsers !== null && editing !== null && (
        <Confirmation
          question={`Change the URL of ${labelOf(movingUsers)}?`}
          consequence="Every user's connection to it is deleted, and each user connects again before the assistant can use it."
          confirmLabel="Yes, change it"
          cancelLabel="Keep the old URL"
          pending={action.pending}
          onConfirm={() => change(movingUsers.name, { url: editing.url })}
          onCancel={() => setMovingUsers(null)}
        />
      )}
      {removing !== null && (
        <Confirmation
          question={`Remove ${labelOf(removing)}?`}
          consequence="Every user's connection to it is deleted, and the assistant stops offering its tools. If you add it again, each user connects again."
          confirmLabel="Yes, remove it"
          cancelLabel="Keep it"
          pending={action.pending}
          onConfirm={() => run(() => data.deleteMcpServer(tenant.id, removing.name))}
          onCancel={() => setRemoving(null)}
        />
      )}
      <ActionError error={action.error} />
      {props.canEdit &&
        (adding ? (
          <AddServerForm
            onAdded={() => {
              setAdding(false);
              props.onChanged();
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button type="button" onClick={() => setAdding(true)}>
            Add server
          </button>
        ))}
    </section>
  );
}

function ServerName({ server }: { server: McpServerRow }) {
  return server.title === null ? (
    <code>{server.name}</code>
  ) : (
    <>
      <strong>{server.title}</strong> <code>{server.name}</code>
    </>
  );
}

function labelOf(server: McpServerRow): string {
  return server.title ?? server.name;
}

function Confirmation(props: {
  question: string;
  consequence: string;
  confirmLabel: string;
  cancelLabel: string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <section className="notice notice-danger" aria-label={props.question}>
      <p>
        <strong>{props.question}</strong> {props.consequence}
      </p>
      <div className="row">
        <button type="button" className="danger" disabled={props.pending} onClick={props.onConfirm}>
          {props.confirmLabel}
        </button>
        <button type="button" className="quiet" onClick={props.onCancel}>
          {props.cancelLabel}
        </button>
      </div>
    </section>
  );
}
